import { useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress, AutocompleteProps } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchTiers, clearTiersData } from "../../reduxStore/TiersSlice";
import { RootState, AppDispatch } from '../../reduxStore/Store';
import { fromEvent } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";
import { Tiers } from "../../models/models";
import { TypeTiers } from "../../constants/enums";
import { labels } from "../../constants/labels";

interface ClientAutoCProps
extends Partial<AutocompleteProps<Tiers, false, false, false>>  {
  onClientChange: (cleint: Tiers | null) => void;
  selectedClient?: Tiers | null;
  required?: boolean;
}

const type = TypeTiers.CLIENT; // Hardcoded type
const limit = 10; // Hardcoded limit
const offset = 0; // Hardcoded offset

const ClientAutoC: React.FC<ClientAutoCProps> = ({ onClientChange, selectedClient, required, ...autocompleteProps }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { tiersData, loading } = useSelector((state: RootState) => state.TiersSlice);
  const [client, setClient] = useState<Tiers | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setClient(selectedClient?.id ? selectedClient : null);
  }, [selectedClient]);

  useEffect(() => {
    if (inputRef.current) {
      const subscription = fromEvent(inputRef.current, "input")
        .pipe(
          debounceTime(500),
          switchMap((event: any) => {
            const nom = event.target.value;
            if (nom.length > 2) {
              dispatch(fetchTiers({ type, nom, limit, offset }));
            } else {
              dispatch(clearTiersData());
            }
            return [];
          })
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [dispatch]);

  const handleChange = (_: any, newValue: Tiers | null) => {
    setClient(newValue);
    onClientChange(newValue);
  };

  return (
    <Autocomplete
      options={tiersData}
      value={client}
      getOptionLabel={(option: Tiers) => `${option.nom} ${option.prenom}`}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      onChange={handleChange}
      onBlur={() => dispatch(clearTiersData())}
      {...autocompleteProps}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={labels.client}
          variant="outlined"
          required={!!required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          size="small"
        />
      )}
    />
  );
};

export default ClientAutoC;